import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Form, { Actions, EmailAddressValidator, RequiredValidator, TextField } from '../../../Components/Form';
import Card from '../../../Components/Containers/Card';
import Icon from '../../../Components/Icons/Icon';
import { useToasts } from '../../../Components/Toasts';
import API from '../../../Services/API';

const Problems = ({ onBack }) => {
    const navigate = useNavigate();
    const { showError } = useToasts();
    const [view, setView] = useState('menu');
    const [done, setDone] = useState(false);

    const handleBack = () => {
        if (view === 'menu') {
            onBack ? onBack() : navigate('/login');
        } else {
            setView('menu');
            setDone(false);
        }
    };

    const reset = (data) => {
        setDone(false);
        return API.post('/auth/requestReset', data).then(() => {
            setDone(true);
        }).catch(err => {
            showError(API.parseErrorResponse(err));
        });
    };

    const renderView = () => {
        switch (view) {
            case 'menu':
                return <MenuView onSelect={setView} />;
            case 'forgot-password':
                return <ForgotPasswordView done={done} onReset={reset} />;
            case 'forgot-email':
                return <ForgotEmailView />;
            case 'lost-mfa':
                return <LostMfaView />;
            default:
                return null;
        }
    };

    return <>
        <img src="/logo-name-horizontal.png"
            alt="getSoapy Logo"
            width="888"
            height="183"
            className="w-[75%] h-auto mx-auto mb-8"
        />

        <Card>
            <button
                onClick={handleBack}
                className="flex flex-row gap-x-2 items-center mb-5 cursor-pointer"
                type="button"
                aria-label="Back"
            >
                <Icon icon="LeftChevron" theme="solid" /> {view === 'menu' ? 'Login' : 'Back'}
            </button>

            {renderView()}
        </Card>
    </>;
};

const MenuView = ({ onSelect }) => {
    return (
        <div className="space-y-2 divide-y">
            <button
                onClick={() => onSelect('forgot-password')}
                className="w-full text-left p-4 cursor-pointer"
                type="button"
            >
                <div className="font-semibold">Forgot password</div>
                <div className="text-sm text-muted mt-1">Reset your password via email</div>
            </button>
            <button
                onClick={() => onSelect('forgot-email')}
                className="w-full text-left p-4 cursor-pointer"
                type="button"
            >
                <div className="font-semibold">Forgot email</div>
                <div className="text-sm text-muted mt-1">Contact us for help</div>
            </button>
            <button
                onClick={() => onSelect('lost-mfa')}
                className="w-full text-left p-4 cursor-pointer"
                type="button"
            >
                <div className="font-semibold">Lost access to MFA device</div>
                <div className="text-sm text-muted mt-1">Use recovery code or contact us</div>
            </button>
        </div>
    );
};

const ForgotPasswordView = ({ done, onReset }) => {
    const navigate = useNavigate();

    const reset = (data) => {
        return onReset(data);
    };

    if (done === true) {
        return (
            <div>
                <p><strong>Check your emails</strong></p>
                <p className="text-muted my-3">
                    We've sent you a link to let you reset your password.<br />
                    If you don't see it, check your spam folder.
                </p>
                <button
                    onClick={() => navigate('/login')}
                    className="link"
                    type="button"
                >
                    Back to login
                </button>
            </div>
        );
    }

    return (
        <Form onSubmit={reset}>
            <TextField type="email" name="email" label="Email" validators={[RequiredValidator, EmailAddressValidator]} />
            <Actions submit={{ label: 'Reset password' }} />
        </Form>
    );
};

const ForgotEmailView = () => {
    return (
        <div className="mt-5">
            <p className="text-muted mb-4">
                If you've forgotten which email address you used to sign up, please contact us and we'll help you recover your account.
            </p>
            <p className="text-muted mb-4">
                Ask for help using the live chat on getSoapy.com, or email us at <a href="mailto:hello@getsoapy.com" className="link">hello@getsoapy.com</a>
            </p>
        </div>
    );
};

const LostMfaView = () => {
    return (
        <div className="mt-5">
            <p className="text-muted mb-4">
                If you've lost access to your multi-factor authentication device, you can use one of your recovery codes when prompted for an MFA code.
            </p>
            <p className="text-muted mb-4">
                If you don't have your recovery codes, please contact us and we'll help you regain access to your account.
            </p>
            <p className="text-muted mb-4">
                Ask for help using the live chat on getSoapy.com, or email us at <a href="mailto:hello@getsoapy.com" className="link">hello@getsoapy.com</a>
            </p>
        </div>
    );
};

export default Problems;
